import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Mail, Smartphone } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export function SecuritySettings() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const method = user?.mfa_method || "email";
  const start = async () => setSetup((await api.post("/auth/mfa/totp/setup")).data);
  const enable = async () => {
    try { await api.post("/auth/mfa/totp/enable", { code }); toast.success(t("Authenticator app enabled")); setSetup(null); setCode(""); refresh(); } catch (e) { toast.error(errorDetail(e)); }
  };
  const useEmail = async () => { await api.post("/auth/mfa/email"); toast.success(t("Email codes enabled")); refresh(); };
  return (
    <div className="card-surface p-5 space-y-3" data-testid="security-settings">
      <div className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" />{t("Two-step verification")}</div>
      <p className="text-sm text-muted-foreground">{t("Required for every login. Choose how you receive your code.")}</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={method === "email" ? "default" : "outline"} onClick={useEmail} data-testid="mfa-method-email"><Mail className="w-4 h-4" />{t("Code by email")}</Button>
        <Button size="sm" variant={method === "totp" ? "default" : "outline"} onClick={start} data-testid="mfa-method-totp"><Smartphone className="w-4 h-4" />{t("Authenticator app")}</Button>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="mfa-current-method">{method === "totp" ? t("Current method: authenticator app") : t("Current method: code by email")}</div>
      {setup && (
        <div className="flex flex-wrap gap-4 items-start pt-2" data-testid="totp-setup">
          <div className="bg-white p-2 rounded-md"><QRCodeSVG value={setup.uri} size={140} /></div>
          <div className="space-y-2 text-sm max-w-xs">
            <p>{t("Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.")}</p>
            <p className="text-xs font-mono break-all" translate="no" data-testid="totp-secret">{setup.secret}</p>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric" maxLength={6} data-testid="totp-code-input" />
            <Button size="sm" disabled={code.length !== 6} onClick={enable} data-testid="totp-enable-button">{t("Activate")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
